import React from 'react';

import { useApplicationContext } from '@common/react/components/Core/Application/Application';
import SearchModal from '@common/react/components/UI/SearchModal/SearchModal';
import { transformArrayToList } from '@common/typescript/objects/List';

import { UserGroupType } from '@commonTuna/react/objects/UserGroup';

import { User, UserRole } from '@app/objects/User';
import SecureRouteWrapper from '@app/components/Routes/SecureRouteWrapper';
import { CustomItem } from '@app/components/Layouts/DashboardLayout/Menu';
import SendMessageButton from '@app/components/UI/SendMessageButton/SendMessageButton';
import { DashboardMenuItem, dashboardTopMenu, getCorrespondence } from '@app/components/Routes/DashboardMenu';
import { useCompanySettingsContext } from '@app/components/UI/CompanySettingsSetter/CompanySettingsSetter';
import { useUserGroupContext } from '@app/components/UI/UserGroupProvider/UserGroupProvider';
import FavoritesButton from '@app/components/UI/FavoritesButton/FavoritesButton';

import '@app/scss/components/searchModal.scss';

enum SearchObjectType {
	Menu = 0,
	User = 1,
	Patient = 2,
	Doctor = 3,
	Location = 4,
}

const SearchObjectTypePathName = {
	[SearchObjectType.User]: 'user',
	[SearchObjectType.Patient]: 'patient',
	[SearchObjectType.Doctor]: 'doctor',
	[SearchObjectType.Location]: 'location',
};

const SearchObjectTypeColor = {
	[SearchObjectType.User]: '#fffde7',
	[SearchObjectType.Patient]: '#ffe1e1',
	[SearchObjectType.Doctor]: '#e1ffe2',
	[SearchObjectType.Location]: '#dce2ff',
};

const allowTransmutationRoles = [UserRole.Admin];

// provide SecureRoutesProviderContext
const SearchModalWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const { checkUserAccess, getUser } = useApplicationContext();
	const { companySettings } = useCompanySettingsContext();
	const user = getUser<User>();
	const { checkUserGroupAccess } = useUserGroupContext();
	const support = checkUserGroupAccess(UserGroupType.Support);
	const isVerificator = checkUserGroupAccess(UserGroupType.SystemVerificator);
	const allowTransmutation = React.useMemo(() => {
		return user?.root || allowTransmutationRoles.some((item) => item === user?.role) || user?.transmutation;
	}, [user, user?.transmutation]);

	return <SecureRouteWrapper
		path="/"
		redirectPath=""
	>
		{({ menu, canAccess }) => {
			const menuItems = React.useMemo(() => {
				const resolvedTopMenu: Array<DashboardMenuItem> = dashboardTopMenu.map((item) => ({
					...item,
					topMenu: true,
					children: item.name === 'Correspondence'
						? transformArrayToList(getCorrespondence(companySettings)
							.filter((menu) => canAccess(menu.path)).map((item) => ({ ...item, topMenu: true })))
						: item.children,
				}))
					.filter((item) => (support || item.path !== '/support-chats') && (isVerificator || item.path !== '/verifications-list'));
				return resolvedTopMenu.concat(menu);
			}, [companySettings]);
			return <SearchModal<SearchObjectType>
				transformItems={(items) => items.map((item) => ({
					...item,
					color: SearchObjectTypeColor[item.type],
				}))}
				getPathFromType={(type) => SearchObjectTypePathName[type]}
				menuItems={menuItems}
				allowTransmutation={allowTransmutation}
				customItem={({
					item,
					menuItem,
					search,
					isSelected,
					ref,
					onSelect,
					onClick,
				}) => <CustomItem
					search={search}
					defaultOpen
					withFavoriteButtons={!(menuItem as any).topMenu}
					className={`${menuItem.className ?? ''} ${isSelected ? 'search-modal-item__selected' : ''}`}
					key={`${item.firstName}${item.path}`}
					item={menuItem}
					withChildren={!!menuItem?.children}
					basePath={menuItem?.basePath || ''}
					forceOpen
					onClick={(e) => onClick?.(e, item)}
					liRef={ref}
					additional={<i className="fa fa-level-down" />}
					onMouseMove={() => !isSelected && !item.group && onSelect && onSelect(item)}
				/>}
				additional={(item) => <>
					<FavoritesButton
						url={`/${SearchObjectTypePathName[item.type]}-editor/${item.id}`}
						title={`${item.firstName} ${item.lastName}`}
						style={{ right: item.type === SearchObjectType.User ? 74 : undefined }}
					/>
					{(item.type === SearchObjectType.User && user?.id !== item.id ? (
						<div
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
							}}
						>
							<SendMessageButton className="btn btn-primary search-modal-item-message-btn" user={item as any}>
								<i className="fa fa-envelope" />
							</SendMessageButton>
						</div>
					) : undefined)}
				</>}
				enable={checkUserAccess([UserRole.Admin, UserRole.Doctor, UserRole.Provider, UserRole.Staff])}
			>
				{children}
			</SearchModal>;
		}}
	</SecureRouteWrapper>;
};

export default SearchModalWrapper;
